import { MouseEvent, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "../classNames.ts";
import { AdminHeyLightAllOrdersScreenPath } from "../../Admin/HeyLight/AdminHeyLightAllOrdersScreen.tsx";
import { AdminHeyLightAllProductScreenPath } from "../../Admin/Product/AdminHeyLightAllProductScreen.tsx";
import { useNavigate } from "react-router";
import { useAdminSidebarCtx } from "./useAdminSidebarCtx.ts";
import {
  Boxes,
  CreditCard,
  HandCoins,
  Landmark,
  LucideIcon,
  Megaphone,
  Package,
  ShoppingBag,
  TicketPercent,
  Undo2,
  X,
} from "lucide-react";
import { AdminLogo } from "./AdminLogo.tsx";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "../Icons/ChevronDoubleIcons.tsx";
import { SignedInUserSection } from "../Layout/SignedInUser.tsx";
import { AdminAllNormasTeachingProductScreenPath } from "../../Admin/NormasTeachingProduct/AdminAllNormasTeachingProductScreen.tsx";
import { AdminStripeAllOrdersScreenPath } from "../../Admin/Stripe/AdminStripeAllOrdersScreen.tsx";
import { AdminAllNormasTeachingBundleScreenPath } from "../../Admin/NormasTeachingBundle/AdminAllNormasTeachingBundleScreen.tsx";
import { AdminAllNormasTeachingCouponScreenPath } from "../../Admin/NormasTeachingCoupon/AdminAllNormasTeachingCouponScreen.tsx";
import { AdminAllPromosScreenPath } from "../../Admin/NormasTeachingPromo/AdminAllPromosScreen.tsx";
import { AllBonificoOrdersScreenPath } from "../../Admin/BonificoOrder/AllBonificoOrdersScreen.tsx";
import { AllWithdrawalRequestsScreenPath } from "../../Admin/Withdrawal/AllWithdrawalRequestsScreen.tsx";
import { AllStripeOrdersCloserScreenPath } from "../../Closer/Stripe/AllStripeOrdersCloserScreen.tsx";
import { AllHeyLightOrdersCloserScreenPath } from "../../Closer/HeyLight/AllHeyLightOrdersCloserScreen.tsx";
import { useAuthCtx } from "../../Auth/useAuthCtx.ts";
import { getRolesFromAuth0User } from "../../Auth/getRolesFromAuth0User.ts";
import { KnownAuth0Roles } from "../../Auth/KnownAuth0Roles.ts";

function colorsOf(isActive: boolean): string {
  return isActive
    ? "bg-primary text-white hover:brightness-110"
    : "hover:bg-slate-200";
}

function isMovingInto(
  element: HTMLElement | null,
  event: MouseEvent<HTMLElement>,
): boolean {
  return (
    !!element &&
    event.relatedTarget instanceof Node &&
    element.contains(event.relatedTarget)
  );
}

function CompactBarButton({
  label,
  icon: Icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  onClick: () => void;
}) {
  const iconButtonRef = useRef<HTMLDivElement>(null);
  const expandedButtonRef = useRef<HTMLDivElement>(null);
  const [expandedButtonPosition, setExpandedButtonPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  function showExpandedButton() {
    const iconButtonBounds = iconButtonRef.current?.getBoundingClientRect();
    if (!iconButtonBounds) return;
    setExpandedButtonPosition({
      top: iconButtonBounds.top,
      left: iconButtonBounds.left,
    });
  }

  function hideExpandedButton() {
    setExpandedButtonPosition(null);
  }

  return (
    <>
      <div
        ref={iconButtonRef}
        aria-label={label}
        onClick={onClick}
        onMouseEnter={showExpandedButton}
        onMouseLeave={(event) => {
          if (!isMovingInto(expandedButtonRef.current, event))
            hideExpandedButton();
        }}
        className={classNames(
          "w-10 h-10 cursor-pointer rounded-lg flex items-center justify-center",
          colorsOf(isActive),
        )}
      >
        <Icon className="w-5 h-5 shrink-0" />
      </div>
      {expandedButtonPosition &&
        createPortal(
          <div
            ref={expandedButtonRef}
            onClick={onClick}
            onMouseLeave={(event) => {
              if (!isMovingInto(iconButtonRef.current, event))
                hideExpandedButton();
            }}
            style={expandedButtonPosition}
            className={classNames(
              "fixed z-50 h-10 pl-2.5 pr-4 cursor-pointer rounded-lg flex items-center space-x-2 whitespace-nowrap shadow-lg",
              isActive ? "bg-primary text-white" : "bg-slate-200",
            )}
          >
            <Icon className="w-5 h-5 shrink-0" />
            <p>{label}</p>
          </div>,
          document.body,
        )}
    </>
  );
}

function BarButton({
  label,
  icon: Icon,
  isActive,
  href,
  compact,
}: {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  href: string;
  compact: boolean;
}) {
  const navigate = useNavigate();
  const { closeOnMobile } = useAdminSidebarCtx();

  function goToPage() {
    navigate(href);
    closeOnMobile();
  }

  if (compact) {
    return (
      <CompactBarButton
        label={label}
        icon={Icon}
        isActive={isActive}
        onClick={goToPage}
      />
    );
  }

  return (
    <div
      onClick={goToPage}
      className={classNames(
        "px-4 cursor-pointer py-2 shadow-slate-200/40 rounded-lg w-full text-center flex items-center space-x-2",
        colorsOf(isActive),
      )}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <p>{label}</p>
    </div>
  );
}

export function AdminSideBar() {
  const { mobileOpen, closeOnMobile, isCollapsed, toggleCollapsed } =
    useAdminSidebarCtx();
  const isCompact = isCollapsed && !mobileOpen;
  const { user } = useAuthCtx();
  const isAdmin = user
    ? getRolesFromAuth0User(user).includes(KnownAuth0Roles.Admin)
    : false;

  // Built inside the component: these screen modules import the sidebar back
  // through AdminMainContainer, so reading the path constants at module scope
  // would resolve them before the cycle is settled.
  const adminEntries = [
    {
      href: AdminAllNormasTeachingProductScreenPath,
      label: "Prodotti Norma's Teaching",
      icon: Package,
    },
    {
      href: AdminAllNormasTeachingBundleScreenPath,
      label: "Bundle Norma's Teaching",
      icon: Boxes,
    },
    {
      href: AdminAllNormasTeachingCouponScreenPath,
      label: "Coupon Norma's Teaching",
      icon: TicketPercent,
    },
    {
      href: AdminAllPromosScreenPath,
      label: "Promo Norma's Teaching",
      icon: Megaphone,
    },
    {
      href: AdminHeyLightAllProductScreenPath,
      label: "Prodotti HeyLight",
      icon: ShoppingBag,
    },
    {
      href: AdminStripeAllOrdersScreenPath,
      label: "Ordini Stripe",
      icon: CreditCard,
    },
    {
      href: AdminHeyLightAllOrdersScreenPath,
      label: "Ordini HeyLight",
      icon: HandCoins,
    },
    {
      href: AllBonificoOrdersScreenPath,
      label: "Ordini Bonifico",
      icon: Landmark,
    },
    {
      href: AllWithdrawalRequestsScreenPath,
      label: "Richieste di recesso",
      icon: Undo2,
    },
  ];

  // Closers only reach the two read-only order screens, and must be pointed at
  // the /closer routes: the /admin ones would bounce them off the page.
  const closerEntries = [
    {
      href: AllStripeOrdersCloserScreenPath,
      label: "Ordini Stripe",
      icon: CreditCard,
    },
    {
      href: AllHeyLightOrdersCloserScreenPath,
      label: "Ordini HeyLight",
      icon: HandCoins,
    },
  ];

  // Driven by the screen first, then by the role: an admin who opens a closer
  // screen gets the closer sidebar, matching the read-only table those screens
  // already render for everyone.
  const isOnCloserScreen = closerEntries.some(
    (entry) => entry.href === window.location.pathname,
  );
  const entries = isOnCloserScreen || !isAdmin ? closerEntries : adminEntries;

  return (
    <div
      className={classNames(
        "bg-white flex-col gap-3 fixed md:sticky top-0 inset-0 z-50 md:h-screen",
        isCompact ? "p-3 items-center" : "p-4 md:min-w-[300px]",
        mobileOpen ? "flex" : "hidden md:flex",
      )}
    >
      <div
        className={classNames(
          "flex items-center gap-3 mb-2",
          isCompact ? "justify-center" : "justify-between",
        )}
      >
        {!isCompact && <AdminLogo />}
        <button
          type={"button"}
          onClick={toggleCollapsed}
          aria-label={isCompact ? "Espandi menu" : "Comprimi menu"}
          title={isCompact ? "Espandi menu" : "Comprimi menu"}
          className={
            "hidden md:block p-2 rounded-lg text-slate-500 hover:bg-slate-100"
          }
        >
          {isCompact ? (
            <ChevronDoubleRightIcon className={"w-5 h-5"} />
          ) : (
            <ChevronDoubleLeftIcon className={"w-5 h-5"} />
          )}
        </button>
        <button
          type={"button"}
          onClick={closeOnMobile}
          aria-label={"Chiudi menu"}
          className={"md:hidden p-1 rounded-md hover:bg-slate-100"}
        >
          <X className={"w-5"} />
        </button>
      </div>
      <div
        className={classNames(
          "flex-1 min-h-0 overflow-y-auto flex flex-col gap-3",
          isCompact ? "items-center" : "",
        )}
      >
        {entries.map((entry) => (
          <BarButton
            key={entry.href}
            href={entry.href}
            isActive={window.location.pathname === entry.href}
            label={entry.label}
            icon={entry.icon}
            compact={isCompact}
          />
        ))}
      </div>
      {mobileOpen && (
        <div className={"pt-4 border-t border-slate-200 md:hidden"}>
          <SignedInUserSection />
        </div>
      )}
    </div>
  );
}
